import React, { useEffect, useState } from "react";
import { encryptId } from "../../../Components/CryptoComponent";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Avatar,
  Typography,
  IconButton,
  Button,
  Chip,
  TablePagination,
  styled,
  tableCellClasses,
  Box,
  TextField,
  Skeleton,
  Autocomplete,
  Grid,
} from "@mui/material";
import {
  Edit,
  Delete,
  Visibility,
  FilterList,
  Add,
  Search,
  RestartAlt,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { axiosAPI } from "../../../Components/AxiosComponents/Axios";
import { errorNotify } from "../../../Components/ToastComponent/ToastNotification";
import SelectInput from "../../../Components/InputComponents/SelectInput";
import { useStaffLayout } from "../../../Components/Context/CandidateLayoutContext";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: `#2C5F8E`,
    color: `#FFFFFF`,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    fontFamily: "Geist, sans-serif",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    fontFamily: "Geist, sans-serif",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: `#EBF2FF`,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
  "&:hover": {
    backgroundColor: "#D6E6F2",
    boxShadow: "0px 0px 5px 0px grey",
  },
}));

const DocumentProcedureList = ({ isAdmin }) => {
  const navigate = useNavigate();
  const { privilegeData } = useStaffLayout();

  function hasDocCheckAddprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName
        .toLowerCase()
        .includes("document checklist");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "add" && priv.assigned === true
      );
    });
  }

  function hasDocCheckEditprivilege(privilegeData) {
    if (isAdmin) return true;
    return privilegeData.some((subModule) => {
      const isClient = subModule.subModuleName
        .toLowerCase()
        .includes("document checklist");
      if (!isClient) return false;

      return subModule.privileges.some(
        (priv) =>
          priv.privilegeName.toLowerCase() === "edit" && priv.assigned === true
      );
    });
  }

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    country: "",
    profession: "",
    documents: [],
  });

  const [data, setData] = useState([]);
  const [professionList, setProfessionList] = useState([]);
  const [countryList, setCountryList] = useState([]);
  const [documentStages, setDocumentStages] = useState([]);

  const [loaderOpen, setLoaderOpen] = useState(true);

  const handleApplyFilters = () => {
    getData();
  };

  const handleResetFilters = () => {
    const resetFilters = {
      country: "",
      profession: "",
      documents: [],
    };

    setFilters({
      country: "",
      profession: "",
      documents: [],
    });

    getData(resetFilters);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getData = (resetFilters = filters) => {
    axiosAPI
      ?.post(`/DocChecklist/document-mapping/grouped`, {
        countryId: resetFilters?.country,
        professionId: resetFilters?.profession,
        documentIds: resetFilters?.documents,
      })
      .then((res) => {
        setData(res?.data?.data);
        setLoaderOpen(false);
      })
      .catch((err) => {
        errorNotify(`Something went wrong`);
      });
  };

  const getCountries = async () => {
    try {
      const res = await axiosAPI.get(`/RecruitmentCountry/list`);
      setCountryList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getProfession = async () => {
    try {
      const res = await axiosAPI.get(`/Profession/active/list`);
      setProfessionList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getDocumentStages = async () => {
    try {
      const res = await axiosAPI.get(`/DocChecklist/list`);
      setDocumentStages(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const navigatetoAdd = () => {
    navigate(`/DocumentProcedure/Add`);
  };

  const navigatetoEdit = (id) => {
    const encryptedpid = encodeURIComponent(encryptId(id?.ProfessionID));
    const encryptedcid = encodeURIComponent(encryptId(id?.CountryID));
    navigate(`/DocumentProcedure/Edit/${encryptedpid}/${encryptedcid}`);
  };

  const navigatetoView = (id) => {
    const encrypted = encodeURIComponent(encryptId(id));
    navigate(`/DocumentProcedure/View/${encrypted}`);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getCountries();
    getProfession();
    getDocumentStages();
  }, []);

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        >
          Document Checklist
        </Typography>

        {/* Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            ml: "auto",
          }}
        >
          {hasDocCheckAddprivilege(privilegeData) && (
            <Button
              variant="contained"
              startIcon={<Add />}
              sx={{ background: "#E75E1F" }}
              onClick={navigatetoAdd}
              disabled={!hasDocCheckAddprivilege(privilegeData)}
            >
              Add
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ maxWidth: "100%", mb: 2 }}>
        <Grid container spacing={2}>
          {/* Country Select */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Country
            </Typography>
            <SelectInput
              value={filters?.country}
              name="country"
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, country: e.target.value }))
              }
              options={countryList?.map((item) => ({
                value: item?.RC_Id,
                label: item?.RC_CountryName,
              }))}
              placeholder="Select Country"
            />
          </Grid>

          {/* Profession Select */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Profession
            </Typography>
            <SelectInput
              value={filters?.profession}
              name="profession"
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, profession: e.target.value }))
              }
              options={professionList?.map((item) => ({
                value: item?.PI_Id,
                label: item?.PI_Name,
              }))}
              placeholder="Select Profession"
            />
          </Grid>

          {/* Documents Autocomplete */}
          <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
            >
              Documents
            </Typography>
            <Autocomplete
              multiple
              disableCloseOnSelect
              options={
                documentStages?.map((item) => ({
                  value: item?.DC_Id,
                  label: item?.DC_Name,
                })) || []
              }
              getOptionLabel={(option) => option.label}
              value={
                documentStages
                  ?.filter((item) => filters?.documents?.includes(item?.DC_Id))
                  .map((item) => ({
                    value: item?.DC_Id,
                    label: item?.DC_Name,
                  })) || []
              }
              onChange={(event, newValue) => {
                const selectedValues = Array.isArray(newValue) ? newValue : [];
                setFilters({
                  ...filters,
                  documents: selectedValues.map((option) => option.value),
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Documents"
                  size="small"
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
      </Box>

      <Box
        sx={{
          width: "100%", // Full width
          display: "flex",
          justifyContent: "flex-end", // Align buttons to the right
          alignItems: "flex-end",
          gap: 1,
          my: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            startIcon={<Search />}
            onClick={handleApplyFilters}
            sx={{
              textTransform: "capitalize",
              color: "white",
              background: "#006CB8",
            }}
          >
            Search
          </Button>

          <Button
            variant="outlined"
            startIcon={<RestartAlt />}
            onClick={handleResetFilters}
            sx={{ textTransform: "capitalize" }}
          >
            Reset
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <TableContainer
        sx={{
          border: "1px solid transparent",
          borderRadius: "10px",
          maxWidth: "100vw",
          overflowX: "auto",
          margin: "auto",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ width: "80px", textAlign: "left" }}>
                #
              </StyledTableCell>
              <StyledTableCell>Country</StyledTableCell>
              <StyledTableCell>Profession</StyledTableCell>
              <StyledTableCell>Documents</StyledTableCell>
              {hasDocCheckEditprivilege(privilegeData) && (
                <StyledTableCell align="center" sx={{ width: "200px" }}>
                  Actions
                </StyledTableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {loaderOpen ? (
              Array.from({ length: 7 }).map((_, idx) => (
                <TableRow key={idx}>
                  {Array.from({ length: 5 }).map((_, index) => (
                    <StyledTableCell key={index}>
                      <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={40}
                      />
                    </StyledTableCell>
                  ))}
                </TableRow>
              ))
            ) : data && data?.length > 0 ? (
              data
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((parent, index) => (
                  <StyledTableRow>
                    <StyledTableCell>{index + 1}</StyledTableCell>
                    <StyledTableCell>{parent?.CountryName}</StyledTableCell>
                    <StyledTableCell>{parent?.ProfessionName}</StyledTableCell>
                    <StyledTableCell>
                      {parent?.Documents?.map((a, index) => {
                        return (
                          <Chip
                            label={a?.DocumentName}
                            sx={{ margin: "5px", border: "1px solid grey" }}
                          />
                        );
                      })}
                    </StyledTableCell>
                    {hasDocCheckEditprivilege(privilegeData) && (
                      <StyledTableCell align="center">
                        <IconButton
                          color="warning"
                          onClick={() => {
                            navigatetoEdit(parent);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </StyledTableCell>
                    )}
                  </StyledTableRow>
                ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Typography
                    variant="body2"
                    sx={{
                      py: 2,
                      fontSize: "20px",
                      fontFamily: "Geist, sans-serif",
                    }}
                  >
                    No data available
                  </Typography>
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[25, 50, 75, 100]}
        sx={{
          // General font size for the pagination root
          fontSize: { xs: "12px", sm: "14px" },
          // Specific font size for the "Rows per page" label
          "& .MuiTablePagination-selectLabel": {
            fontSize: { xs: "12px", sm: "14px" },
            fontFamily: "Geist, sans-serif", // Optional
          },
        }}
      />
    </Paper>
  );
};

export default DocumentProcedureList;
